import { useEffect, useState, useMemo } from "react";
import api, { fmtMoney, fmtNum, formatErr } from "@/lib/api";
import { sumFleetPnL, computeDashboardOrdersStats } from "@/lib/liveMetrics";
import { computeLiveClientStats } from "@/lib/overviewStats";
import { useWS } from "@/contexts/WebSocketContext";
import { toast } from "sonner";
import {
  TrendUp, TrendDown, Wallet, ChartLineUp, ShieldWarning, Pulse as Activity,
  Stack, CheckCircle, ShoppingCart, Scales, Circle, Lightning,
} from "@phosphor-icons/react";
import RefreshButton from "@/components/layout/RefreshButton";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";
import GroupBadge from "@/components/GroupBadge";
import PositionAlertModal, { POSITION_ALERT_THRESHOLD } from "@/components/PositionAlertModal";

const KPI = ({ icon: Icon, label, value, sub, accent = "default", testid }) => {
  const valueClass =
    accent === "green" ? "text-[#10B981]" :
    accent === "red" ? "text-[#FF3B30]" :
    "text-app-text";

  return (
    <div className="app-card p-4 sm:p-6 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
        <div className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-app-subtle truncate">{label}</div>
        <Icon size={16} className="text-app-muted shrink-0" />
      </div>
      <div className={`font-mono-d text-[clamp(1.25rem,4vw,1.875rem)] font-semibold tabular-nums break-words ${valueClass}`}>
        {value}
      </div>
      {sub && <div className="font-mono-d text-[11px] text-app-muted mt-2">{sub}</div>}
    </div>
  );
};

function ClientStatRow({ row, maxOrders, maxLots }) {
  const label = row.group || row.label || "Unassigned";
  const orderPct = maxOrders > 0 ? Math.round((row.orders / maxOrders) * 100) : 0;
  const lotPct = maxLots > 0 ? Math.round((row.lots / maxLots) * 100) : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr_5rem_5rem] gap-3 sm:gap-4 items-center py-3 border-b border-app-border/70 last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        <GroupBadge name={row.group || label} color={row.color} />
      </div>
      <div className="space-y-2 min-w-0">
        <div>
          <div className="flex justify-between text-[10px] font-mono-d uppercase text-app-muted mb-1">
            <span>Orders</span>
            <span>{row.orders}</span>
          </div>
          <div className="client-stat-bar">
            <div className="client-stat-bar-fill" style={{ width: `${orderPct}%`, backgroundColor: row.color || "#94A3B8" }} />
          </div>
        </div>
        <div>
          <div className="flex justify-between text-[10px] font-mono-d uppercase text-app-muted mb-1">
            <span>Lots</span>
            <span>{fmtNum(row.lots)}</span>
          </div>
          <div className="client-stat-bar">
            <div className="client-stat-bar-fill opacity-70" style={{ width: `${lotPct}%`, backgroundColor: row.color || "#94A3B8" }} />
          </div>
        </div>
      </div>
      <div className="text-right sm:text-center">
        <div className="text-[10px] font-mono-d uppercase text-app-muted">Orders</div>
        <div className="font-mono-d text-lg tabular-nums">{row.orders}</div>
      </div>
      <div className="text-right">
        <div className="text-[10px] font-mono-d uppercase text-app-muted">Lots</div>
        <div className="font-mono-d text-lg tabular-nums">{fmtNum(row.lots)}</div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { registrationTick, status: wsStatus, liveMetrics, liveVersion } = useWS();
  const [data, setData] = useState(null);
  const [fleet, setFleet] = useState([]);
  const [error, setError] = useState(null);
  const [positionAlertDismissed, setPositionAlertDismissed] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/overview");
      setData(data);
      setFleet(data.fleet || []);
      setError(null);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail));
      toast.error("Failed to load overview");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [registrationTick]);
  useEffect(() => {
    const interval = wsStatus === "connected" ? 60000 : 10000;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [wsStatus]);

  const display = useMemo(() => {
    if (!data) return null;
    const live = fleet.length ? sumFleetPnL(fleet, liveMetrics) : {};
    const clientStats = computeLiveClientStats(fleet, liveMetrics);
    const dashboardStats = computeDashboardOrdersStats(fleet, liveMetrics);
    return {
      ...data,
      ...live,
      ...clientStats,
      ...dashboardStats,
    };
  }, [data, fleet, liveMetrics, liveVersion]);

  const maxClientOrders = useMemo(
    () => Math.max(0, ...(display?.by_client || []).map((r) => r.orders || 0)),
    [display?.by_client],
  );
  const maxClientLots = useMemo(
    () => Math.max(0, ...(display?.by_client || []).map((r) => r.lots || 0)),
    [display?.by_client],
  );

  const highPositionAccounts = useMemo(() => {
    if (!fleet.length) return [];
    return fleet
      .map((acc) => {
        const live = liveMetrics[acc.id];
        const positions = Number(
          live?.open_positions ?? live?.positions_count ?? acc.open_positions ?? 0,
        );
        return { ...acc, positions };
      })
      .filter((acc) => acc.positions >= POSITION_ALERT_THRESHOLD)
      .sort((a, b) => b.positions - a.positions);
  }, [fleet, liveMetrics, liveVersion]);

  useEffect(() => {
    if (highPositionAccounts.length === 0) {
      setPositionAlertDismissed(false);
    }
  }, [highPositionAccounts.length]);

  const showPositionAlert = highPositionAccounts.length > 0 && !positionAlertDismissed;

  const manualRefresh = async () => {
    await load();
    toast.success("Overview refreshed");
  };

  if (!display && !error) return <PageShell><div className="text-app-muted font-mono-d text-xs">LOADING...</div></PageShell>;
  if (!display) return <PageShell><div className="text-[#FF3B30] font-mono-d text-xs">{error || "FAILED TO LOAD"}</div></PageShell>;

  const dailyAccent = display.total_daily_pl >= 0 ? "green" : "red";
  const floatAccent = display.total_floating_pl >= 0 ? "green" : "red";
  const dashboardMtmAccent = (display.dashboard_mtm ?? 0) >= 0 ? "green" : "red";

  return (
    <PageShell>
      {showPositionAlert && (
        <PositionAlertModal
          accounts={highPositionAccounts}
          onClose={() => setPositionAlertDismissed(true)}
        />
      )}
      <PageHeader eyebrow="Command Center" title="OVERVIEW">
        <div className="flex flex-col items-stretch sm:items-end gap-2.5 w-full lg:w-auto">
          <span
            className={`inline-flex self-end items-center gap-1.5 px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border ${
              wsStatus === "connected"
                ? "border-[#10B981]/35 text-[#10B981] bg-[#10B981]/8"
                : "border-[#FF3B30]/35 text-[#FF3B30] bg-[#FF3B30]/8"
            }`}
          >
            <Circle size={6} weight="fill" />
            Floating P/L · {wsStatus === "connected" ? "Live ~1s" : "Poll 10s"}
          </span>
          <RefreshButton onRefresh={manualRefresh} testId="overview-refresh-btn" />
        </div>
      </PageHeader>

      <div className="responsive-grid mb-4 sm:mb-6">
        <KPI icon={Stack} label="Total Accounts" value={display.total_accounts} sub={`${display.ea_connected ?? 0} EA online`} testid="kpi-total-accounts" />
        <KPI icon={CheckCircle} label="Enabled" value={display.active_accounts} sub={`${display.disabled_accounts} disabled`} accent="green" testid="kpi-active" />
        <KPI icon={Wallet} label="Total Balance" value={fmtMoney(display.total_balance)} testid="kpi-balance" />
        <KPI icon={ChartLineUp} label="Total Equity" value={fmtMoney(display.total_equity)} testid="kpi-equity" />
      </div>

      <div className="responsive-grid mb-4 sm:mb-6">
        <KPI icon={display.total_floating_pl >= 0 ? TrendUp : TrendDown} label="Floating P/L (Total)" value={fmtMoney(display.total_floating_pl)} accent={floatAccent} testid="kpi-floating" />
        <KPI
          icon={(display.dashboard_mtm ?? 0) >= 0 ? TrendUp : TrendDown}
          label="Dashboard Orders MTM"
          value={fmtMoney(display.dashboard_mtm ?? 0)}
          accent={dashboardMtmAccent}
          sub={`${display.dashboard_orders_count ?? 0} orders (${display.dashboard_buy_count ?? 0}B / ${display.dashboard_sell_count ?? 0}S) · ${display.dashboard_lots ?? 0}L`}
          testid="kpi-dashboard-mtm"
        />
        <KPI icon={Activity} label="Daily P/L" value={fmtMoney(display.total_daily_pl)} accent={dailyAccent} testid="kpi-daily" />
        <KPI icon={Activity} label="Net Performance" value={fmtMoney(display.total_equity - display.total_balance)} accent={(display.total_equity - display.total_balance) >= 0 ? "green" : "red"} testid="kpi-net" />
      </div>

      <div className="responsive-grid mb-4 sm:mb-6">
        <KPI icon={ShoppingCart} label="Total Open Orders" value={display.total_orders ?? 0} sub={`${display.accounts_with_orders ?? 0} accounts with positions`} testid="kpi-total-orders" />
        <KPI icon={Scales} label="Total Open Lots" value={fmtNum(display.total_lots ?? 0)} sub="Fleet-wide lot exposure" testid="kpi-total-lots" />
        <KPI icon={ShieldWarning} label="Avg Drawdown" value={`${fmtNum(display.avg_drawdown)}%`} sub="Across fleet" testid="kpi-drawdown" />
        <KPI icon={Lightning} label="Dashboard Lots" value={fmtNum(display.dashboard_lots ?? 0)} sub={`Magic 90001 · ${display.dashboard_orders_count ?? 0} orders`} testid="kpi-dashboard-lots" />
      </div>

      <section className="app-card p-4 sm:p-6" data-testid="client-trade-stats">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
          <div>
            <div className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-app-subtle mb-1">Client breakdown</div>
            <h2 className="font-display font-bold text-lg sm:text-xl text-app-text">Orders & lots by group</h2>
          </div>
          <div className="font-mono-d text-[10px] text-app-muted uppercase tracking-widest">
            Live open positions · updates via WebSocket
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-[8rem_1fr_5rem_5rem] gap-4 pb-2 border-b border-app-border text-[10px] font-mono-d uppercase tracking-widest text-app-subtle">
          <div>Client</div>
          <div>Share</div>
          <div className="text-center">Orders</div>
          <div className="text-right">Lots</div>
        </div>

        {(display.by_client || []).length === 0 ? (
          <div className="py-8 text-center text-app-muted font-mono-d text-xs">NO CLIENT DATA</div>
        ) : (
          (display.by_client || []).map((row) => (
            <ClientStatRow
              key={row.group || row.label || "unassigned"}
              row={row}
              maxOrders={maxClientOrders}
              maxLots={maxClientLots}
            />
          ))
        )}
      </section>
    </PageShell>
  );
}
